import { createFileRoute, Link } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import { Button, buttonVariants, Skeleton } from "@rumbledirect/ui-v2";
import { RequirePermission } from "@/currentUser/RequirePermission";
import type {
  SelectorSelectorQueryRevisionResourceSelectorQueryRevisionOutputJsonld as Revision,
  SelectorSelectorQueryResourceQueryOutputJsonld as Query,
} from "@/lib/api/generated/model";
import {
  useGetAdminSelectorQueriesId,
  useGetAdminSelectorRevisionsId,
} from "@/lib/api/generated/selector/selector";
import {
  activationOf,
  SelectorVersionEditor,
  type SelectorVersionEditorProps,
} from "@/steering/SelectorVersionEditor";
import { fromConfiguration } from "@/steering/selectorEditorDraft";
import {
  isEditable,
  revisionLabel,
  revisionState,
} from "@/steering/selectorRevisions";

export const Route = createFileRoute(
  "/_authenticated/steering_/$queryId_/revisions/$revisionId/edit",
)({
  component: EditSelectorRevisionPage,
});

function EditSelectorRevisionPage(): ReactNode {
  return (
    <RequirePermission
      permission="admin_selector_query_update"
      fallback={
        <p role="alert">Je hebt geen rechten om versies te bewerken.</p>
      }
    >
      <EditSelectorRevision />
    </RequirePermission>
  );
}

function EditSelectorRevision(): ReactNode {
  const { queryId, revisionId } = Route.useParams();
  const query = useGetAdminSelectorQueriesId(queryId);
  const revision = useGetAdminSelectorRevisionsId(revisionId);

  if (query.isPending || revision.isPending)
    return (
      <div role="status">
        <span className="sr-only">Versie wordt geladen…</span>
        <Skeleton className="h-9 w-72" />
      </div>
    );
  if (query.isError || revision.isError)
    return (
      <div role="alert" className="py-6">
        <p>De versie kon niet worden geladen.</p>
        <Button
          className="mt-4"
          variant="outline"
          onClick={() => {
            void query.refetch();
            void revision.refetch();
          }}
        >
          Opnieuw proberen
        </Button>
      </div>
    );

  const state = revisionState(revision.data, query.data.activeRevision);
  if (!isEditable(state))
    return (
      <div role="alert" className="flex flex-col items-start gap-4 py-6">
        <p>
          Deze versie is al actief geweest en kan niet meer worden gewijzigd.
          Maak een nieuwe versie op basis hiervan.
        </p>
        <Link
          to="/steering/$queryId/new"
          params={{ queryId }}
          search={{ basedOn: revisionId }}
          className={buttonVariants()}
        >
          Nieuwe versie op basis hiervan
        </Link>
      </div>
    );
  return (
    <EditRevisionEditor
      key={revisionId}
      query={query.data}
      revision={revision.data}
      badge={
        state === "draft"
          ? { label: "Concept", variant: "neutral" }
          : { label: "Gepland", variant: "progress" }
      }
    />
  );
}

function EditRevisionEditor({
  query,
  revision,
  badge,
}: {
  readonly query: Query;
  readonly revision: Revision;
  readonly badge: SelectorVersionEditorProps["badge"];
}): ReactNode {
  const navigate = Route.useNavigate();
  const [draft] = useState(() => fromConfiguration(revision.configuration));
  const toDetail = (id: string) =>
    void navigate({
      to: "/steering/$queryId",
      params: { queryId: query.id },
      search: { revision: id },
    });
  return (
    <SelectorVersionEditor
      queryId={query.id}
      queryName={query.name}
      badge={badge}
      base={{ label: revisionLabel(revision), draft }}
      initialDraft={draft}
      revisionId={revision.id}
      initialActivation={activationOf(revision.revisionAt)}
      initialRevisionAt={revision.revisionAt}
      onDone={(saved) => toDetail(saved.id)}
      onCancel={() => toDetail(revision.id)}
    />
  );
}
